/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import InputAdornment from '@mui/material/InputAdornment';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDDatePicker from 'components/MDDatePicker';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import SearchProduct from 'layouts/receive/SearchProduct';

const initAttr = {
  id: '',
  list_id: '',
  receive_id: '',
  product_id: '',
  sku: '',
  code: '',
  image: '',
  name: '',
  location_id: '',
  location_name: '',
  unit_name: '',
  barcode: '',
  product_name: '',
  price: '',
  quantity: '',
  amount: '',
  unit_id: '',
  warehouse_id: '',
  zone_id: '',
  lot: '',
  discount_value: '',
  discount_type: 'B',
  is_expire: 'N',
  date_expire: Func.dateNow(),
  created_at: '',
  updated_at: '',
};

const initParent = {
  id: '',
  code: '',
  bill_no: '',
  branch_id: '',
  supplier_id: '',
  user_id: '',
  po_id: '',
  poauto_id: '0',
  location_id: '',
  image_id: '',
  date_shipping: '0000-00-00',
  date_due: '0000-00-00',
  deposit: '0.00',
  vat_id: '2',
  discount: '0.00',
  discount_value: '0.00',
  discount_type: 'B',
  discount_unit: 'Y',
  total: '',
  shipping: '',
  amount: '',
  vat: '',
  vat_value: '',
  amount_vat: '',
  nettotal: '',
  paid: '',
  balance: '',
  remark: '',
  kind: 'WEB',
  add_stock: 'N',
  print_no: '0',
  checkall: 'N',
  status: 'edit',
  created_at: '',
  updated_at: '',
  po_supplier: '',
  po_code: '',
  request_pr: 'N',
  po_remark: '',
  po_status: '',
  po_date: '',
  po_user: '',
  poauto_code: '',
  excel: '',
  print: '',
  date_record: Func.dateNow(),
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

const ReturnStoreList = forwardRef(({ openPhoto }, ref) => {
  const mod = 'return_store_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [attr, setAttr] = useState(initAttr);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);
  const [parent, setParent] = useState(initParent);
  const [discountChange, setDiscountChange] = useState('');
  const [shippingChange, setShippingChange] = useState('');
  const [depositChange, setDepositChange] = useState('');
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);
  const txtQuantity = useRef(null);
  const tableRef = useRef(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const scrollLeftRight = () => {
    if (tableRef.current.scrollLeft > 0) {
      tableRef.current.scrollLeft = 0;
    } else {
      tableRef.current.scrollLeft += 1000;
    }
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
      }
    });
  };

  const open = (item) => {
    setAttr(item);
    setModalEdit(true);
    setConfirm(false);
  };

  const addConfirm = () => {
    if (!Func.verifyLength(attr.barcode_id, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setConfirm(false);
    setLoading(true);
    setModalAdd(false);

    FetchData('PUT', `/${mod}`, app.token, {
      return_id: id,
      barcode_id: attr.barcode_id,
      quantity: attr.quantity,
      lot: attr.lot,
      is_expire: attr.is_expire,
      date_expire: attr.date_expire,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const addInit = (item) => {
    setConfirm(false);
    setAttr({
      ...item,
      quantity: item.quantity,
      lot: '',
      is_expire: 'N',
      date_expire: Func.dateNow(),
    });
    setModalAdd(true);

    setTimeout(function () {
      txtQuantity.current.focus();
    }, 500);
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.quantity, 1)) return;

    setConfirm(true);
  };

  const edit = () => {
    setConfirm(false);
    setLoading(true);
    setModalEdit(false);

    FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
      quantity: attr.quantity,
      lot: attr.lot,
      is_expire: attr.is_expire,
      date_expire: attr.date_expire,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        setModalEdit(true);
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscount = (item) => {
    FetchData('OPTIONS', `/receive/discount`, app.token, {
      ref_id: id,
      discount_value: item.discount_value,
      discount_type: item.discount_type,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendDeposit = () => {
    if (parent.deposit * 1 === depositChange * 1) return;

    setDepositChange(parent.deposit);

    FetchData('OPTIONS', `/${mod}/deposit`, app.token, {
      ref_id: id,
      deposit: parent.deposit,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendType = (type) => {
    setParent({ ...parent, discount_type: type });
    editDiscount({ discount_value: parent.discount_value, discount_type: type });
  };

  const sendValue = () => {
    if (parent.discount_value * 1 !== discountChange * 1) {
      setDiscountChange(parent.discount_value);
      editDiscount({ discount_value: parent.discount_value, discount_type: parent.discount_type });
    }
  };

  const sendShipping = () => {
    if (parent.shipping * 1 === shippingChange * 1) return;

    setShippingChange(parent.shipping);

    FetchData('OPTIONS', `/receive/shipping`, app.token, {
      ref_id: id,
      shipping: parent.shipping,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountUnit = (input) => {
    FetchData('OPTIONS', `/${mod}/editdiscount`, app.token, {
      id: parent.id,
      discount_unit: input,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.${result.msg}`), 'error');
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const expireDisplay = (item) => {
    if (item.is_expire === 'N') return '-';

    return Func.dateDisplay(item.date_expire);
  };

  const btnEdit = (item) => {
    if (parent.status === 'cancel' || parent.status === 'success') return null;

    return (
      <MDButton variant='text' color='success' size='small' iconOnly onClick={() => open(item)}>
        <Icon>border_color</Icon>
      </MDButton>
    );
  };

  const btnDel = (item) => {
    if (parent.status === 'cancel' || parent.status === 'success') return null;

    return (
      <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell>LOT</TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            จำนวนคืน
          </TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {record.map((row) => (
          <TableRow key={row.id} hover>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell>
              <MDBox className='nowrap'>
                {row.code} <span className='color-default'>{row.sku}</span>
              </MDBox>
              <MDBox className='nowrap'>{row.name}</MDBox>
            </TableCell>
            <TableCell>
              <MDBox className='nowrap'>{row.lot}</MDBox>
              <MDBox className='nowrap'>{expireDisplay(row)}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox>{Func.numberDisplay(row.quantity)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='center'>{btnEdit(row)}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={3}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox p={2} pt={3}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>
          {parent.status === 'cancel' || parent.status === 'success' ? null : (
            <Grid item xs={6}>
              <MDBox display='flex' justifyContent='flex-end' gap={1} p={2}>
                <SearchProduct add={addInit} />
              </MDBox>
            </Grid>
          )}
          <Grid item xs={12}>
            <TableContainer
              sx={{ boxShadow: 0, minHeight: 300 }}
              ref={tableRef}
              onDoubleClick={scrollLeftRight}
            >
              <Table aria-label='simple table'>
                {renderHeader()}
                {renderRecord()}
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={5}>
        <Grid item xs={12} md={12} lg={6}>
          <Paper sx={{ p: 2 }} variant='outlined'>
            {attr.image === '' ? null : (
              <MDBox component='img' src={attr.image} alt='' width='100%' height='100%' />
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                label='รหัสสินค้า'
                value={attr.code}
                variant='standard'
                fullWidth
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='สินค้า'
                value={attr.name}
                variant='standard'
                fullWidth
                multiline
                inputProps={{
                  autoComplete: 'off',
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='จำนวนคืน'
                onChange={(e) => setAttr({ ...attr, quantity: e.target.value })}
                onFocus={(e) => e.target.select()}
                value={attr.quantity}
                variant='standard'
                type='number'
                fullWidth
                InputProps={{
                  inputRef: txtQuantity,
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position='start'>
                      <span>{attr.unit_name}</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    autoComplete: 'off',
                    className: 'text-right',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                label='LOT'
                onChange={(e) => setAttr({ ...attr, lot: e.target.value })}
                value={attr.lot}
                variant='standard'
                fullWidth
                InputProps={{
                  autoComplete: 'off',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={attr.is_expire === 'Y'}
                    onChange={(e) => setAttr({ ...attr, is_expire: e.target.checked ? 'Y' : 'N' })}
                    inputProps={{
                      'aria-label': 'enable',
                    }}
                  />
                }
                label='เพิ่มวันหมดอายุ'
              />
            </Grid>
            {attr.is_expire === 'Y' ? (
              <Grid item xs={12} sm={6}>
                <MDDatePicker
                  onChange={([date]) => {
                    setAttr({ ...attr, date_expire: Func.dateFormat(date) });
                  }}
                  value={new Date(attr.date_expire)}
                  input={{
                    label: 'วันหมดอายุ',
                    variant: 'standard',
                    fullWidth: true,
                  }}
                />
              </Grid>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='md' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)} : {attr.barcode}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='info' onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderEdit = () => {
    return (
      <Dialog open={modalEdit} maxWidth='md' keepMounted aria-describedby='dialog-edit' fullWidth>
        <DialogTitle>
          {t(`element.edit`)} #{attr.id}
          <IconButton
            aria-label='close'
            onClick={() => setModalEdit(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox p={3}>{renderInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={edit}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant='text' color='success' onClick={editConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      view,
    };
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderAdd()}
      {renderEdit()}
      {renderPhoto()}
      {renderLoading()}
      {notify}
    </div>
  );
});

ReturnStoreList.propTypes = {
  openPhoto: PropTypes.func.isRequired,
};

export default ReturnStoreList;
